import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useIntl, FormattedMessage, FormattedDate } from 'react-intl';
import { get } from '../../services/api';
import { useNotifier } from '../../contexts/NotificationContext';
import useDebounce from '../../hooks/useDebounce';
import { showBackButton } from "../../services/telegramService.js";

import Title from '../../components/common/Title/Title';
import Input from '../../components/common/Input/Input';
import Button from '../../components/common/Button/Button';
import FullScreenMessage from '../../components/common/FullScreenMessage/FullScreenMessage';
import SubmissionListItem from '../../components/SubmissionListItem/SubmissionListItem';
import styles from './AnswersListPage.module.css';
import { FiDownload, FiInbox, FiSearch } from 'react-icons/fi';

const AnswersListPage = () => {
    const { formId } = useParams();
    const navigate = useNavigate();
    const intl = useIntl();
    const { addNotification } = useNotifier();

    // --- НАЧАЛО НОВОЙ, УПРОЩЕННОЙ ЛОГИКИ СОСТОЯНИЯ ---
    const [submissions, setSubmissions] = useState([]);
    const [page, setPage] = useState(1);
    const [pageInfo, setPageInfo] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isAppending, setIsAppending] = useState(false);

    const [isExporting, setIsExporting] = useState(false); // Состояние для блокировки кнопки

    const [filter, setFilter] = useState('all'); // 'all' или 'unread'
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    // --- КОНЕЦ НОВОЙ ЛОГИКИ СОСТОЯНИЯ ---

    useEffect(() => {
        showBackButton();
    }, []);

    // --- ЕДИНЫЙ useEffect ДЛЯ ЗАГРУЗКИ ДАННЫХ ---
    useEffect(() => {
        page === 1 ? setIsLoading(true) : setIsAppending(true);

        const params = {
            page,
            filter: filter === 'all' ? null : filter
        };

        get(`forms/${formId}/submissions`, params)
            .then(response => {
                const responseData = response.data;
                if (responseData && responseData.items) {
                    // Если это первая страница, заменяем данные. Если нет - добавляем.
                    setSubmissions(prev => page === 1 ? responseData.items : [...prev, ...responseData.items]);
                    setPageInfo({ links: responseData.links, meta: responseData.meta });
                }
            })
            .catch(error => addNotification(error.message, 'error'))
            .finally(() => {
                setIsLoading(false);
                setIsAppending(false);
            });

    }, [formId, page, filter, debouncedSearchTerm, addNotification]);

    // --- НОВЫЕ ОБРАБОТЧИКИ ДЛЯ ФИЛЬТРОВ ---
    const handleFilterChange = (newFilter) => {
        setFilter(newFilter);
        setPage(1); // Сбрасываем на первую страницу
        setSubmissions([]); // Очищаем старые результаты
    };

    const handleSearchChange = (e) => {
        const newSearchTerm = e.target.value;
        setSearchTerm(newSearchTerm);
        setPage(1); // Сбрасываем на первую страницу при поиске
        setSubmissions([]); // Очищаем старые результаты
    };

    const loadMoreSubmissions = () => {
        // Просто увеличиваем номер страницы
        setPage(prevPage => prevPage + 1);
    };

    const handleExport = () => {
        setIsExporting(true);
        addNotification(intl.formatMessage({ id: "answersPage.export.start" }), 'info');

        get(`forms/${formId}/submissions/export`)
            .then(response => {
                if (response.message) {
                    addNotification(response.message, 'success');
                }
            })
            .catch(error => {
                // --- ИСПРАВЛЕНИЕ ЗДЕСЬ ---
                // Заменяем жестко закодированную строку на ключ локализации
                const errorMessage = error.message || intl.formatMessage({ id: "answersPage.export.error" });
                addNotification(errorMessage, 'error');
            })
            .finally(() => {
                setTimeout(() => {
                    setIsExporting(false);
                }, 3000);
            });
    };

    const hasMorePages = pageInfo?.meta && pageInfo.meta.current_page < pageInfo.meta.last_page;

    return (
        <div className={styles.pageContainer}>
            <div className={styles.header}>
                <Title><FormattedMessage id="answersPage.title"/></Title>
                {submissions.length > 0 && (
                    // Передаем isExporting в disabled
                    <Button onClick={handleExport} variant="secondary" disabled={isExporting} size="min">
                        {isExporting
                            ? <FormattedMessage id="answersPage.export.inProgress" defaultMessage="В процессе..."/>
                            : <><FiDownload/> <FormattedMessage id="answersPage.exportButton"/></>
                        }
                    </Button>
                )}
            </div>

            <div className={styles.filterContainer}>
                <button
                    className={`${styles.filterButton} ${filter === 'all' ? styles.active : ''}`}
                    onClick={() => handleFilterChange('all')}
                >
                    <FormattedMessage id="answers.filter.all"/>
                </button>
                <button
                    className={`${styles.filterButton} ${filter === 'unread' ? styles.active : ''}`}
                    onClick={() => handleFilterChange('unread')}
                >
                    <FormattedMessage id="answers.filter.unread"/>
                </button>
            </div>

            <div className={styles.listContainer}>
                {isLoading && submissions.length === 0 ? (
                    <div className={styles.loaderContainer}>
                        <div className={styles.spinner}></div>
                    </div>
                ) : submissions.length > 0 ? (
                    submissions.map((sub, index) => {
                        return (
                            <SubmissionListItem
                                key={sub.id} // key должен оставаться уникальным ID из базы
                                submission={sub}
                                formId={formId}
                            />
                        );
                        // --- КОНЕЦ НОВОЙ ЛОГИКИ ---
                    })
                ) : (
                    <FullScreenMessage
                        icon={<FiInbox size={48}/>}
                        title={intl.formatMessage({id: "answersPage.noAnswers"})}
                    />
                )}
            </div>

            {hasMorePages && (
                <div className={styles.pagination}>
                    <Button onClick={loadMoreSubmissions} disabled={isAppending}>
                        {isAppending ? '...' : <FormattedMessage id="answersPage.loadMore" />}
                    </Button>
                </div>
            )}

        </div>
    );
};

export default AnswersListPage;
